import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookMedical,
  faSignOutAlt,
  faChevronDown,
  faBars,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { AuthContext } from "../../Context/AuthContext";
import { useCart } from "../../Context/CartContext";
import PatientFooter from "./PatientFooter";
import styles from "./PatientPortal.module.css";
import ChatWindow from "./ChatWindow";
import logoBg from "../../static/logobgr.png";

const PatientPortalLayout = ({
  children,
  activePage = "home",
  patientName = "Patient",
  totalAppointments = 0,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isNavChatOpen, setIsNavChatOpen] = useState(false);
  const [chatDoctor, setChatDoctor] = useState(null);
  const { logout } = useContext(AuthContext);
  const { cartCount } = useCart();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleOpenGlobalChat = async () => {
    setMenuOpen(false);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/doctor/all", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.doctors && response.data.doctors.length > 0) {
        setChatDoctor(response.data.doctors[0]);
        setIsNavChatOpen(true);
      }
    } catch (error) {
      console.error("Error launching chat from nav:", error);
    }
  };

  // Generate clean 1-2 letter avatar initials
  const getInitials = (name) => {
    if (!name || name === "Patient") return "P";
    const parts = name.trim().split(" ");
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0][0] ? parts[0][0].toUpperCase() : "P";
  };

  return (
    <div className={styles.portalWrapper}>
      {/* ─── FLOATING PERSISTENT ROUNDED NAVBAR ──────────────────────── */}
      <header className={styles.topNavbar}>
        <Link to="/" className={styles.brandLink}>
          <img src={logoBg} alt="ReformMe Logo" className={styles.brandLogoImg} />
          <span>ReformMe Healthcare</span>
        </Link>

        <div className={styles.navRightCluster}>
          {/* Quick Profile Dropdown Trigger (Leftmost of controls) */}
          <div className={styles.profileWrap}>
            <button
              type="button"
              onClick={() => {
                setDropdownOpen(!dropdownOpen);
                if (menuOpen) setMenuOpen(false);
              }}
              className={styles.profileBtn}
              aria-label="User menu dropdown"
            >
              <div className={styles.avatarCircle}>{getInitials(patientName)}</div>
              <span className={styles.patientNameText}>{patientName}</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{ fontSize: "0.75rem", color: "#94a3b8" }}
              />
            </button>

            {dropdownOpen && (
              <div className={styles.dropdownMenu}>
                <button
                  type="button"
                  onClick={handleLogout}
                  className={styles.dropdownItem}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>

          {/* Hamburger Menu Cluster (Rightmost) */}
          <div className={styles.hamburgerWrap}>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(!menuOpen);
                if (dropdownOpen) setDropdownOpen(false);
              }}
              className={styles.hamburgerBtn}
              aria-label="Navigation Menu"
            >
              <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
            </button>

            {menuOpen && (
              <div className={styles.hamburgerMenu}>
                <Link
                  to="/patient-dashboard"
                  onClick={() => setMenuOpen(false)}
                  className={`${styles.menuItem} ${activePage === "home" ? styles.menuItemActive : ""
                    }`}
                >
                  <span>Overview</span>
                </Link>

                <Link
                  to="/myappointment"
                  onClick={() => setMenuOpen(false)}
                  className={`${styles.menuItem} ${activePage === "appointments" ? styles.menuItemActive : ""
                    }`}
                >
                  <span>Appointments</span>
                  {totalAppointments > 0 && (
                    <span className={styles.countBadge}>{totalAppointments}</span>
                  )}
                </Link>

                <Link
                  to="/patient-orders"
                  onClick={() => setMenuOpen(false)}
                  className={`${styles.menuItem} ${activePage === "orders" ? styles.menuItemActive : ""
                    }`}
                >
                  <span>My Current Orders</span>
                </Link>

                <Link
                  to="/patient-profile"
                  onClick={() => setMenuOpen(false)}
                  className={`${styles.menuItem} ${activePage === "profile" ? styles.menuItemActive : ""
                    }`}
                >
                  <span>My Profile</span>
                </Link>

                <button
                  type="button"
                  onClick={handleOpenGlobalChat}
                  className={styles.menuItem}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    color: "inherit",
                    cursor: "pointer",
                  }}
                >
                  <span>Doctor Chat</span>
                </button>

                <Link
                  to="/shop"
                  onClick={() => setMenuOpen(false)}
                  className={`${styles.menuItem} ${activePage === "shop" ? styles.menuItemActive : ""}`}
                >
                  <span>Wellness Shop</span>
                  {cartCount > 0 && (
                    <span className={styles.countBadge}>{cartCount}</span>
                  )}
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ─── MAIN PORTAL CONTENT GRID ────────────────────────────────── */}
      <main className={styles.mainContainer}>
        {children}
      </main>

      <PatientFooter onOpenChat={handleOpenGlobalChat} onLogout={handleLogout} />

      {/* ─── GLOBAL NAVBAR CHAT PORTAL ───────────────────────────────── */}
      {isNavChatOpen &&
        chatDoctor &&
        createPortal(
          <ChatWindow
            doctor={chatDoctor}
            onClose={() => setIsNavChatOpen(false)}
          />,
          document.body
        )}
    </div>
  );
};

export default PatientPortalLayout;
